/**
 * Pure derivations over one card document. No React, no I/O — which is what
 * makes the region rules unit-testable without DOM choreography.
 *
 * Every lookup here goes through `state/cardIndex`, which builds one hash index
 * per document and caches it on the document's identity. The signatures are
 * unchanged from when each of these was a `find`/`filter`/`includes` over the
 * card's arrays — deliberately, so the change is a property of the read path and
 * not a migration every call site has to take part in. What changed is the cost
 * (O(n) per question → O(1)) and the fact that the arrays returned are now
 * **stable across renders**, which is what lets memoisation downstream work.
 *
 * The collections are shared with the index. Treat them as read-only.
 */

import { indexCard, regionBlocks } from '../state';
import {
  ApplicationRef,
  BlockPlacement,
  FieldId,
  InfoRow,
  PhotoAnchor,
  ProductCard,
  RegionId,
} from '../types';

/** Placements in one region, in render order. */
export const selectRegionBlocks = (
  card: ProductCard,
  region: RegionId
): readonly BlockPlacement[] => regionBlocks(card, region);

/** Info rows after side-by-side pairing has been resolved (spec R51). */
export const selectInfoRows = (card: ProductCard): readonly InfoRow[] =>
  indexCard(card).infoRows;

/** Photo overlays grouped by anchor, one entry per anchor (spec R52). */
export const selectPhotoBlocksByAnchor = (
  card: ProductCard
): Readonly<Record<PhotoAnchor, readonly BlockPlacement[]>> => indexCard(card).photoByAnchor;

/** Blocks pinned to the footer strip (spec R53). */
export const selectFloatingBlocks = (card: ProductCard): readonly BlockPlacement[] =>
  regionBlocks(card, 'floating');

export const selectPlacement = (
  card: ProductCard,
  fieldId: FieldId
): BlockPlacement | undefined => indexCard(card).placementByField.get(fieldId);

export const selectIsFieldEnabled = (card: ProductCard, fieldId: FieldId): boolean =>
  indexCard(card).enabled.has(fieldId);

/**
 * Why a card cannot be deleted: the applications still referencing it (spec
 * R25). An empty result means deletion is allowed. Kept as a tested selector
 * rather than inline dialog logic so the server can re-enforce the same rule
 * (threat model, abuse case 3).
 */
// Pick<> rather than the whole card: the listing's slim rows carry references
// too, and the delete rule must be one definition for both shapes.
export const selectDeleteBlockers = (
  card: Pick<ProductCard, 'references'>
): ApplicationRef[] => [...card.references];

export const selectIsDeletable = (card: Pick<ProductCard, 'references'>): boolean =>
  selectDeleteBlockers(card).length === 0;
